import {
  Alert,
  Box,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { ReactElement } from "react";

import {
  ToggleOptOutSharingMutation,
  ToggleOptOutSharingMutationVariables,
} from "~frontend/__generated__/graphql";
import { LOGGED_IN_INFO } from "~frontend/graphql/loggedInInfo";
import { TOGGLE_OPT_OUT_SHARING } from "~frontend/graphql/tenant";
import {
  useCurrentUserIsStaff,
  useTenant,
} from "~frontend/providers/LoggedInUserProvider";
import { useNonCacheClearingApolloClient } from "~frontend/providers/NonCacheClearingApolloClientProvider";
import useMutation from "~frontend/util/useMutation";

export const OptOutSharing = ({
  isGlobal,
}: {
  isGlobal: boolean;
}): ReactElement => {
  const tenant = useTenant()!;
  const isStaff = useCurrentUserIsStaff();
  const confirm = useConfirm();
  const client = useNonCacheClearingApolloClient();

  const [updateOptOutListSharing] = useMutation<
    ToggleOptOutSharingMutation,
    ToggleOptOutSharingMutationVariables
  >(TOGGLE_OPT_OUT_SHARING, {
    onErrorSnackbarMessage: "Error toggling Opt Out sharing",
    refetchQueries: [
      {
        query: LOGGED_IN_INFO,
      },
    ],
    client,
  });

  return (
    <Box>
      {isStaff && isGlobal ? (
        <FormControlLabel
          label="(Staff-Only Control) Share Opt Outs between teams in this Organization"
          control={
            <Switch
              color="primary"
              checked={tenant.optOutSharingEnabled}
              onChange={async () => {
                const verb = tenant.optOutSharingEnabled ? "Disable" : "Enable";
                const { confirmed } = await confirm({
                  title: `${verb} Opt Out sharing?`,
                  description: `Are you sure you want to ${verb.toLowerCase()} Opt Out sharing? This could have a dramatic impact on current and future campaigns for all subaccounts.`,
                });
                if (!confirmed) {
                  return;
                }

                await updateOptOutListSharing({
                  variables: {
                    input: {
                      enableOptOutSharing: !tenant.optOutSharingEnabled,
                    },
                  },
                });
              }}
            />
          }
        />
      ) : null}
      {isGlobal ? (
        <>
          {tenant.optOutSharingEnabled ? (
            <Alert severity="info" icon={false}>
              <Typography variant="body1">
                <strong>Note:</strong> By default, all Opt Outs are shared among
                every subaccount in your organization (this does not apply to
                temporary Strike Lists, which apply only to their own
                subaccount).
                <br />
                <br />
                Opt Outs that don't belong to any specific subaccount can be
                uploaded here, and are also shared among every subaccount in
                your organization (but can only be deleted from this page). To
                turn sharing behavior off, please reach out to{" "}
                <a href="mailto:support@scaletowin.com">
                  support@scaletowin.com
                </a>
                .
              </Typography>
            </Alert>
          ) : (
            <Alert severity="info" icon={false}>
              <Typography variant="body1">
                Note: Your organization has turned off shared Opt Outs. Opt Outs
                from one subaccount will not appear in other subaccounts, and
                vice versa. Strike Lists are never shared among subaccounts.{" "}
                <br />
                <br />
                Opt Outs uploaded to this Global Opt Outs page will apply to
                every subaccount, and can only be deleted from this page. To
                turn shared Opt Outs behavior on, please reach out to{" "}
                <a href="mailto:support@scaletowin.com">
                  support@scaletowin.com
                </a>
                .
              </Typography>
            </Alert>
          )}
        </>
      ) : (
        <>
          {tenant.optOutSharingEnabled ? (
            <Alert severity="info" icon={false}>
              <Typography variant="body1">
                <strong>Note:</strong> By default, all Opt Outs are shared among
                every subaccount in your organization. Opt Outs from{" "}
                {isGlobal ? "one" : "this"} subaccount will appear in other
                subaccounts, and vice versa. This does not apply to Strike
                Lists. To turn sharing behavior off, please reach out to{" "}
                <a href="mailto:support@scaletowin.com">
                  support@scaletowin.com
                </a>
                .
              </Typography>
            </Alert>
          ) : (
            <Alert severity="info" icon={false}>
              <Typography variant="body1">
                Note: Your organization has turned off shared Opt Outs. Opt Outs
                from this subaccount will not appear in other subaccounts, and
                vice versa. Strike Lists are never shared among subaccounts. To
                turn shared Opt Outs behavior on, please reach out to{" "}
                <a href="mailto:support@scaletowin.com">
                  support@scaletowin.com
                </a>
                .
              </Typography>
            </Alert>
          )}
        </>
      )}
    </Box>
  );
};
